import { Link } from "react-router-dom";
import { ArrowRight, Gauge, MapPin, Compass, Megaphone, Search, Target } from "lucide-react";
import { SERVICES } from "@/data/services";
import { FadeUp, Eyebrow } from "@/lib/motion";

const ICONS = {
  Search,
  Gauge,
  MapPin,
  Compass,
  Target,
  Megaphone,
};

function Card({ s, className }) {
  const Icon = ICONS[s.icon];
  return (
    <Link
      to={s.route || `/${s.slug}`}
      data-testid={`service-card-${s.num}`}
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-coal/70 p-6 transition-all duration-500 hover:-translate-y-1 hover:border-flame/50 hover:shadow-[0_20px_60px_rgba(255,85,0,0.12)] md:p-8 ${className || ""}`}
    >
      <div className="relative flex flex-col">
        <div className="flex items-start justify-between">
          <div className="glass flex h-12 w-12 items-center justify-center rounded-xl border border-slate-200">
            <Icon className="h-5 w-5 text-flame" />
          </div>
          <span className="font-mono text-base text-faint">{s.num}</span>
        </div>
        <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
          {s.title}
        </h3>
        <p className="mt-3 text-lg leading-relaxed text-mist md:text-xl">
          {s.copy}
        </p>
      </div>
      {s.img && (
        <div className="relative mt-6 min-h-[190px] flex-1 overflow-hidden rounded-xl border border-slate-100">
          <img
            src={s.img}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-white via-white/25 to-transparent" />
        </div>
      )}
    </Link>
  );
}

export default function Services() {
  return (
    <section id="services" className="relative bg-ink py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <FadeUp>
          <Eyebrow>What we do</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
            Five levers. <span className="text-flame">One strategy.</span>
          </h2>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-mist md:text-xl">
            SEO alone is never the whole answer. Every lever has a job — and
            strategy is how we keep them all pulling the same direction.
          </p>
        </FadeUp>

        <FadeUp delay={0.15}>
          <div
            data-testid="services-bento-grid"
            className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-6 md:gap-6"
          >
            <Card s={SERVICES[0]} className="md:col-span-4 md:row-span-2 min-h-[320px]" />
            <Card s={SERVICES[1]} className="md:col-span-2" />
            <Card s={SERVICES[2]} className="md:col-span-2" />
            <Card s={SERVICES[3]} className="md:col-span-3" />
            <Card s={SERVICES[4]} className="md:col-span-3" />
          </div>
        </FadeUp>

        <FadeUp delay={0.2}>
          <div
            data-testid="services-strategy-band"
            className="mt-6 overflow-hidden rounded-2xl bg-[#0B0F19] p-8 md:p-12"
          >
            <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
              <div className="max-w-2xl">
                <div className="font-mono text-[13px] uppercase tracking-[0.25em] text-flame">
                  Strategy
                </div>
                <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-white md:text-3xl">
                  The work that connects the levers.
                </h3>
                <p className="mt-4 text-lg leading-relaxed text-slate-300">
                  Cards are just levers. Strategy is the plan for pulling them
                  together — one map across your website, SEO, Google Ads and
                  social campaigns, reviewed with Todd one-on-one, so every
                  click has a job and every month compounds.
                </p>
              </div>
              <Link
                to="/coaching-consulting"
                data-testid="services-strategy-button"
                className="group inline-flex shrink-0 items-center gap-2 rounded-2xl bg-flame px-6 py-3.5 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)]"
              >
                Strategy &amp; consulting
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
